import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import createSocketConnection from "../../services/socket-io";
import { AuthContext } from "../Auth/AuthContext";

const RESYNC_THROTTLE_MS = 2500;

const SocketContext = createContext({
  socket: null,
  isConnected: false,
  syncVersion: 0,
  requestResync: () => {},
});

const readRealtimeDebugEnabled = () => {
  try {
    return localStorage.getItem("debugRealtime") === "true";
  } catch (_err) {
    return false;
  }
};

export const SocketProvider = ({ children }) => {
  const { isAuth } = useContext(AuthContext);
  const socketRef = useRef(null);
  const hasConnectedRef = useRef(false);
  const lastResyncAtRef = useRef(0);
  const [socketInstance, setSocketInstance] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [syncVersion, setSyncVersion] = useState(0);

  const debugEnabled = readRealtimeDebugEnabled();

  const debugLog = useCallback(
    (event, payload = {}) => {
      if (!debugEnabled) {
        return;
      }

      // Dedicated realtime logs to troubleshoot intermittent UI desync.
      console.debug("[realtime]", event, {
        at: new Date().toISOString(),
        ...payload,
      });
    },
    [debugEnabled]
  );

  const requestResync = useCallback(
    (reason = "manual", force = false) => {
      const now = Date.now();
      const elapsed = now - lastResyncAtRef.current;
      if (!force && elapsed < RESYNC_THROTTLE_MS) {
        debugLog("resync_skipped_throttle", { reason, elapsedMs: elapsed });
        return;
      }

      lastResyncAtRef.current = now;
      debugLog("resync_started", { reason });
      setSyncVersion((prev) => prev + 1);
      debugLog("resync_done", { reason });
    },
    [debugLog]
  );

  useEffect(() => {
    if (!isAuth) {
      setIsConnected(false);
      hasConnectedRef.current = false;
      setSocketInstance(null);
      if (socketRef.current) {
        socketRef.current.removeAllListeners();
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      return undefined;
    }

    if (!socketRef.current) {
      socketRef.current = createSocketConnection();
      setSocketInstance(socketRef.current);
    }

    const socket = socketRef.current;

    const handleConnect = () => {
      setIsConnected(true);
      debugLog("socket_connect", { id: socket.id });
      if (hasConnectedRef.current) {
        requestResync("socket_reconnect");
      } else {
        hasConnectedRef.current = true;
      }
    };

    const handleDisconnect = (reason) => {
      setIsConnected(false);
      debugLog("socket_disconnect", { reason });
    };

    const handleReconnect = (attempt) => {
      debugLog("socket_reconnect", { attempt });
      requestResync("socket_reconnect_event");
    };

    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);
    if (socket.io) {
      socket.io.on("reconnect", handleReconnect);
    }

    return () => {
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
      if (socket.io) {
        socket.io.off("reconnect", handleReconnect);
      }
    };
  }, [debugLog, isAuth, requestResync]);

  const value = useMemo(
    () => ({
      socket: socketInstance,
      isConnected,
      syncVersion,
      requestResync,
    }),
    [isConnected, requestResync, socketInstance, syncVersion]
  );

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
};

export const useSocket = () => useContext(SocketContext);
